<template>
  <div v-if="show && warnings.length" class="alert alert-warning mb-2" role="alert">
    <div class="d-flex align-items-start">
      <IconAlertTriangle class="icon alert-icon me-2 flex-shrink-0" />
      <div>
        <h4 class="alert-title mb-1">{{ __DICT__.accommodation?.behaviourWarnings || 'Behaviour warnings' }}</h4>
        <div v-for="warning in warnings" :key="warning">{{ warning }}</div>
      </div>
    </div>
  </div>
  <Card v-if="show" :title="title" show-status-bar margin="mb-2">
      <template v-if="accommodation">
        <DetailField :label="__DICT__.passengerView.name"
                     :value="accommodation.name || (__DICT__.notAvailable || 'Not available')" />
        <DetailField :label="__DICT__.passengerView.address">
          {{ accommodation.address || (__DICT__.notAvailable || 'Not available') }}
          <a :href="googleMapsLink" target="_blank" class="btn btn-icon btn-sm btn-ghost-primary ms-1">
            <IconMapPin/>
          </a>
        </DetailField>
        <DetailField :label="__DICT__.passengerView.room">
          {{ accommodation.subblock_physical_name || (__DICT__.notAvailable || 'Not available') }}
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          <span class="text-muted small">(ID: {{ accommodation.subblock_id }})</span>
          <router-link v-if="showLink"
                       :to="`/accommodation/${accommodation.subblock_id}`"
                       class="btn btn-icon btn-sm btn-ghost-primary ms-2">
            <IconSquareArrowRight/>
          </router-link>
        </DetailField>
        <DetailField :label="__DICT__.passengerView.roomRep">
          <template v-if="accommodation.subblock_room_reps && accommodation.subblock_room_reps.length">
            <span v-for="(rep, index) in accommodation.subblock_room_reps" :key="rep.id">
              {{ rep.name }}<span v-if="index < accommodation.subblock_room_reps.length - 1">, </span>
            </span>
            <router-link v-for="rep in accommodation.subblock_room_reps"
                         :key="rep.id"
                         :to="`/rep/${rep.id}`"
                         class="btn btn-icon btn-sm btn-ghost-primary ms-1">
              <IconSquareArrowRight/>
            </router-link>
          </template>
          <template v-else>{{ __DICT__.notAvailable || 'Not available' }}</template>
        </DetailField>
        <DetailField :label="__DICT__.passengerView.inspectionRep">
          <template v-if="accommodation.subblock_inspection_rep && accommodation.subblock_inspection_rep.length">
            <span v-for="(rep, index) in accommodation.subblock_inspection_rep" :key="rep.id">
              {{ rep.name }}<span v-if="index < accommodation.subblock_inspection_rep.length - 1">, </span>
            </span>
            <router-link v-for="rep in accommodation.subblock_inspection_rep"
                         :key="rep.id"
                         :to="`/rep/${rep.id}`"
                         class="btn btn-icon btn-sm btn-ghost-primary ms-1">
              <IconSquareArrowRight/>
            </router-link>
          </template>
          <template v-else>{{ __DICT__.tbc || 'TBC' }}</template>
        </DetailField>
        <DetailField v-if="keyStatus?.numberOfKeys"
                     :label="__DICT__.accommodation?.numberOfKeys || 'Nº Keys'"
                     :value="keyStatus.numberOfKeys" />
        <DetailField v-if="keyStatus?.extraKeys"
                     :label="__DICT__.accommodation?.extraKeys || 'Extra Keys'"
                     :value="keyStatus.extraKeys" />
        <DetailField v-if="keyStatus?.supplier"
                     :label="__DICT__.accommodation?.supplier || 'Supplier'"
                     :value="keyStatus.supplier" />
        <DetailField v-if="keyStatus?.distLoc"
                     :label="__DICT__.accommodation?.distributionLocation || 'Distribution Location'">
          {{ keyStatus.distLoc }}
          <a :href="distMapsLink(keyStatus.distLat, keyStatus.distLon)" target="_blank"
             class="btn btn-icon btn-sm btn-ghost-primary ms-1">
            <IconMapPin/>
          </a>
        </DetailField>
        <DetailField v-if="keyStatus?.roomId"
                     :label="__DICT__.accommodation?.roomKey || 'Room Key'">
          <Badge v-if="keyStatus.ready && !keyStatus.distributed" :text="__DICT__.accommodation?.ready || 'Ready'" variant="success" pill />
          <Badge v-if="!keyStatus.ready" :text="__DICT__.accommodation?.notReady || 'Not Ready'" variant="secondary" pill />
          <Badge v-if="keyStatus.blocked" :text="__DICT__.accommodation?.blocked || 'Blocked'" variant="danger" pill />
          <Badge v-if="keyStatus.distributed && !keyStatus.returned" :text="__DICT__.accommodation?.distributed || 'Distributed'" variant="purple" pill />
          <Badge v-if="keyStatus.returned" :text="__DICT__.accommodation?.returned || 'Returned'" variant="orange" pill />
        </DetailField>
        <DetailField v-if="keyStatus?.distributed && keyStatus?.distributedTo"
                     :label="__DICT__.accommodation?.distributedTo || 'Distributed to'">
          <router-link class="link-azure" :to="`/passenger/${keyStatus.distributedTo?.split(' - ')[1] || ''}`">
            {{ keyStatus.distributedTo }}
          </router-link>
        </DetailField>
        <DetailField v-if="showRef" :label="__DICT__.passengerView.reference"
                     :value="accommodation.reference || (__DICT__.notAvailable || 'Not available')" />
        <DetailField :label="__DICT__.capacity"
                     :value="accommodation.subblock_capacity || (__DICT__.notAvailable || 'Not available')" />
        <DetailField :label="__DICT__.passengerView.amenities">
          <ul class="list-unstyled m-0">
            <li v-if="accommodation.bed_linen_included === 1">{{ __DICT__.bedLinenIncluded }}</li>
            <li v-if="accommodation.towels_included === 1">{{ __DICT__.towelsIncluded }}</li>
          </ul>
        </DetailField>
        <DetailField :label="__DICT__.accommodation?.linenConfig || 'Linen Config'">
          <span v-if="keyStatus?.linenConfig">{{ keyStatus.linenConfig }}</span>
        </DetailField>
        <DetailField v-if="accommodation.passenger_notes" :label="__DICT__.passengerView.notes">
          <SafeHtml :html="accommodation.passenger_notes" />
        </DetailField>
        <DetailField v-if="accommodation.contact_number" :label="__DICT__.contactNumber"
                     :value="accommodation.contact_number" />
      </template>
      <p v-else>{{ __DICT__.passengerView.noAccommodationInfoAvailable }}</p>
  </Card>
  <Card v-if="show && stayInformation.length"
        :title="__DICT__.accommodation?.stayInformation || 'Stay information'"
        show-status-bar margin="mb-2">
    <DetailField v-for="item in stayInformation" :key="item.field" :label="item.label">
      <SafeHtml :html="item.html" />
    </DetailField>
  </Card>
</template>

<script setup>
import {computed} from 'vue'
import {IconAlertTriangle, IconMapPin, IconSquareArrowRight} from '@tabler/icons-vue'
import {useWeekStore} from "@/stores/week.js";
import { Card, Badge, DetailField, SafeHtml } from "@/components/atomic";
import { STAY_INFORMATION_FIELDS, behaviourWarnings } from '@/utils/accommodationUtils.js'

const weekStore = useWeekStore()


const props = defineProps({
  accommodation: {
    type: Object,
    required: true
  },
  showRef: {
    type: Boolean,
    default: false
  },
  showLink: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: __DICT__.passengerView.accommodation
  },
  show: {
    type: Boolean,
    default: true
  },
  keyStatus: {
    type: Object,
  },
  // Every copy of the room's accommodation (one per guest), for behaviour warnings; defaults to this one
  roomAccommodations: {
    type: Array,
    default: null
  }
})

const warnings = computed(() => behaviourWarnings(props.roomAccommodations || [props.accommodation]))

const stayInformation = computed(() => {
  if (!props.accommodation) return []
  return STAY_INFORMATION_FIELDS
    .filter(({ field }) => typeof props.accommodation[field] === 'string' && props.accommodation[field].trim())
    .map(({ field, key, fallback }) => ({
      field,
      label: __DICT__.accommodation?.[key] || fallback,
      html: props.accommodation[field]
    }))
})



const googleMapsLink = computed(() => {
  if (!props.accommodation.latitude || !props.accommodation.longitude) return '#'
  return `https://www.google.com/maps?q=${props.accommodation.latitude},${props.accommodation.longitude}`
})


const distMapsLink = (lat, lon) => {
  return `https://www.google.com/maps?q=${lat},${lon}`
}
</script>
